import Phaser from 'phaser';
import type { LevelDef } from '../../content/levelSchema';
import { gameAudio } from '../audio/audio';
import { label, UI } from './text';
import { thumbColor } from './thumbColors';
import { drawCoin } from './widgets';

export { thumbColor };

/**
 * The navy UI material (docs/ART_DIRECTION.md): panels, beveled buttons, bold stars and lawn
 * mini maps. UI is navy; the world never is.
 */

/** A navy panel: white outer rule, periwinkle inner line, ink inside edge. */
export function navyPanel(
  g: Phaser.GameObjects.Graphics,
  x: number,
  y: number,
  w: number,
  h: number,
): void {
  g.fillStyle(UI.shadow, 0.6).fillRect(x + 2, y + 2, w, h);
  g.fillStyle(UI.panel).fillRect(x, y, w, h);
  g.lineStyle(1, UI.rule).strokeRect(x + 0.5, y + 0.5, w - 1, h - 1);
  g.lineStyle(1, UI.panelHi).strokeRect(x + 1.5, y + 1.5, w - 3, h - 3);
  g.lineStyle(1, UI.shadow).strokeRect(x + 2.5, y + 2.5, w - 5, h - 5);
}

/**
 * Clears a scene for a redraw by destroying everything on it. (`children.removeAll(true)` only
 * takes objects off the display list: they stay alive, still answer taps and keep their timers.)
 */
export function clearScene(scene: Phaser.Scene): void {
  for (const obj of [...scene.children.list]) obj.destroy();
}

/**
 * A beveled button: primary is yellow with navy text, secondary periwinkle with white text.
 * Presses 1 px down with the click sound; fires on release over it.
 */
export function bevelButton(
  scene: Phaser.Scene,
  x: number,
  y: number,
  w: number,
  h: number,
  text: string,
  onPress: () => void,
  primary = false,
  scale: 1 | 2 = 1,
  /** Draw a coin icon in the label: the index of a two-space gap left for it (prices). */
  coinAt?: number,
): Phaser.GameObjects.Container {
  const g = scene.add.graphics();
  const draw = (down: boolean) => {
    g.clear();
    const dy = down ? 1 : 0;
    if (!down) g.fillStyle(UI.shadow).fillRect(1, 2, w, h);
    g.fillStyle(primary ? UI.accent : UI.panelHi).fillRect(0, dy, w, h);
    g.lineStyle(1, UI.shadow).strokeRect(0.5, dy + 0.5, w - 1, h - 1);
    g.fillStyle(0xffffff, primary ? 0.55 : 0.35).fillRect(1, dy + 1, w - 2, 1);
    t.setY(Math.round((h - 7 * scale) / 2) + dy);
    if (coinAt !== undefined) drawCoin(g, t.x + (coinAt * 6 + 3) * scale, t.y);
  };
  const t = label(scene, 0, 0, text, scale, primary ? UI.panel : UI.text);
  if (primary) t.setDropShadow(0, 0, 0, 0);
  t.setX(Math.round((w - t.width) / 2));
  const c = scene.add.container(x, y, [g, t]);
  draw(false);
  c.setSize(w, h);
  // Phaser tests a container's hit area with the pointer shifted by its displayOrigin (half its
  // size), so the rectangle is offset to cover exactly the drawn button (+2 px slop).
  c.setInteractive({
    hitArea: new Phaser.Geom.Rectangle(w / 2 - 2, h / 2 - 2, w + 4, h + 4),
    hitAreaCallback: (area: Phaser.Geom.Rectangle, px: number, py: number) => area.contains(px, py),
    useHandCursor: true,
  });
  c.on('pointerdown', () => draw(true));
  c.on('pointerout', () => draw(false));
  c.on('pointerup', () => {
    draw(false);
    gameAudio.play('click');
    onPress();
  });
  return c;
}

/**
 * Page arrows for a grid too long for the screen: "< 1/2 >" right-aligned at (right, y), on a
 * section header's line. Each arrow has a thumb-sized tap target. Nothing for a single page.
 */
export function pager(
  scene: Phaser.Scene,
  right: number,
  y: number,
  page: number,
  pages: number,
  onPage: (page: number) => void,
): void {
  if (pages <= 1) return;
  const next = label(scene, right, y, '>', 1, UI.text).setOrigin(1, 0);
  const count = label(scene, next.x - 12, y, `${page + 1}/${pages}`, 1, UI.dim).setOrigin(1, 0);
  const prev = label(scene, count.x - count.width - 6, y, '<', 1, UI.text).setOrigin(1, 0);
  for (const [arrow, by, name] of [
    [prev, -1, 'PREV PAGE'],
    [next, 1, 'NEXT PAGE'],
  ] as const) {
    scene.add
      .zone(arrow.x - arrow.width / 2 - 9, y - 4, 18, 16)
      .setOrigin(0)
      .setData('label', name)
      .setInteractive({ useHandCursor: true })
      .on('pointerup', () => {
        gameAudio.play('click');
        onPage((page + by + pages) % pages);
      });
  }
}

/**
 * "Press this": the button flashes, two bright lights chase around its border, two rings ripple
 * out from it and it hops (no hop with `reducedMotion`). About a second.
 */
export function callAttention(
  scene: Phaser.Scene,
  target: Phaser.GameObjects.Container,
  w: number,
  h: number,
  reducedMotion = false,
): void {
  const x = Math.round(target.x);
  const y = Math.round(target.y);
  // The chase runs on a loop 3 px outside the button (and its drop shadow).
  const left = x - 3;
  const top = y - 3;
  const right = x + w + 2;
  const bottom = y + h + 3;
  const rw = right - left;
  const rh = bottom - top;
  const perimeter = 2 * (rw + rh);
  const at = (d: number): [number, number] => {
    const p = ((Math.round(d) % perimeter) + perimeter) % perimeter;
    if (p < rw) return [left + p, top];
    if (p < rw + rh) return [right, top + (p - rw)];
    if (p < 2 * rw + rh) return [right - (p - rw - rh), bottom];
    return [left, bottom - (p - 2 * rw - rh)];
  };
  const lights = scene.add.graphics().setDepth(target.depth + 1);
  const TAIL = 18;
  scene.tweens.addCounter({
    from: 0,
    to: perimeter * 2,
    duration: 1100,
    ease: 'Quad.easeOut',
    onUpdate: (tw) => {
      const head = tw.getValue() ?? 0;
      const fade = tw.progress > 0.8 ? (1 - tw.progress) / 0.2 : 1;
      lights.clear();
      for (const start of [0, perimeter / 2]) {
        for (let k = TAIL; k >= 0; k--) {
          const [px, py] = at(start + head - k * 2);
          const color = k === 0 ? 0xffffff : UI.accent;
          lights.fillStyle(color, fade * (1 - k / (TAIL + 1))).fillRect(px - 1, py - 1, 2, 2);
        }
      }
    },
    onComplete: () => lights.destroy(),
  });
  // Rings rippling out.
  for (let i = 0; i < 2; i++) {
    const ring = scene.add.graphics().setDepth(target.depth + 1);
    scene.tweens.addCounter({
      from: 0,
      to: 3,
      delay: 80 + i * 280,
      duration: 480,
      ease: 'Quad.easeOut',
      onUpdate: (tw) => {
        const grow = Math.round(tw.getValue() ?? 0);
        ring.clear();
        ring
          .lineStyle(2, UI.accent, 1 - tw.progress)
          .strokeRect(x - grow - 1, y - grow - 1, w + grow * 2 + 2, h + grow * 2 + 2);
      },
      onComplete: () => ring.destroy(),
    });
  }
  // Two quick flashes over the button.
  const flash = scene.add.rectangle(0, 0, w, h, 0xffffff, 1).setOrigin(0).setAlpha(0);
  target.add(flash);
  scene.tweens.add({
    targets: flash,
    alpha: { from: 0.7, to: 0 },
    duration: 260,
    repeat: 1,
    repeatDelay: 60,
    onComplete: () => flash.destroy(),
  });
  // A hop, in whole pixels.
  if (!reducedMotion) {
    scene.tweens.killTweensOf(target);
    target.setY(y);
    scene.tweens.addCounter({
      from: 0,
      to: Math.PI * 2,
      duration: 560,
      onUpdate: (tw) => {
        const v = tw.getValue() ?? 0;
        target.setY(y - Math.round(Math.max(0, Math.sin(v)) * 3));
      },
      onComplete: () => target.setY(y),
    });
  }
}

const BIG_STAR = ['...#...', '..#y#..', '###yy##', '#yyyyy#', '.#yyy#.', '.#y#y#.', '##...##'];

/** A bold 7 x 7 star with an ink outline (level plates, cards, results), `scale` px per pixel. */
export function drawBigStar(
  g: Phaser.GameObjects.Graphics,
  x: number,
  y: number,
  filled: boolean,
  scale = 1,
): void {
  BIG_STAR.forEach((row, dy) => {
    for (let dx = 0; dx < row.length; dx++) {
      const ch = row[dx];
      if (ch === '.') continue;
      g.fillStyle(ch === '#' ? UI.shadow : filled ? UI.accent : 0x3a3366);
      g.fillRect(x + dx * scale, y + dy * scale, scale, scale);
    }
  });
}

/**
 * A mini map of a lawn's real layout, `px` pixels per tile, as a texture (cached by key). `done`
 * draws its grass mowed; locked lawns are darkened by the caller.
 */
export function lawnThumbnail(
  scene: Phaser.Scene,
  level: LevelDef,
  px: number,
  done: boolean,
): string {
  const key = `thumb:${level.id}:${px}:${done ? 1 : 0}`;
  if (scene.textures.exists(key)) return key;
  const { width: w, height: h } = level.sim;
  const tex = scene.textures.createCanvas(key, w * px, h * px);
  if (!tex) return key;
  const ctx = tex.context;
  level.art.forEach((tile, i) => {
    const x = i % w;
    const y = Math.floor(i / w);
    const color = thumbColor(tile, x, y, done && tile === 'grass');
    ctx.fillStyle = `#${color.toString(16).padStart(6, '0')}`;
    ctx.fillRect(x * px, y * px, px, px);
  });
  tex.refresh();
  return key;
}

/** The largest whole pixels-per-tile at which `level` fits in maxW x maxH (at least 1). */
export function thumbnailScale(level: LevelDef, maxW: number, maxH: number): number {
  return Math.max(
    1,
    Math.min(Math.floor(maxW / level.sim.width), Math.floor(maxH / level.sim.height)),
  );
}
